import React, { useEffect, useRef, useState } from "react";
import { API } from "@/lib/api";
import { MessageCircle, X, Send, Sparkles, RotateCcw } from "lucide-react";

const COPY = {
  customer: {
    title: "Delux Concierge",
    sub: "Ask about rates, fleet & booking",
    greeting: "Hi! I'm the Delux Limo concierge. Ask me about our fleet, rates, airport transfers, hourly service or how to book — I'm happy to help.",
    placeholder: "Ask about rates, vehicles, booking…",
  },
  dispatcher: {
    title: "Dispatch Assistant",
    sub: "AI ops help · trips, drafts & summaries",
    greeting: "Ready to help. Try: \"Summarize today's trips\", \"Which trips are unassigned?\" or \"Draft a delay text for a customer\".",
    placeholder: "Summarize today's trips…",
  },
};

function newSessionId() {
  return `ai_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

// Minimal markdown: **bold** and [label](url) → styled nodes (everything else stays plain text)
function renderContent(text) {
  if (!text) return text;
  // Normalize markdown bullets ("* item" / "- item") to a clean dot
  text = text.replace(/^([ \t]*)[*+-][ \t]+/gm, "$1• ");
  const regex = /\*\*(.+?)\*\*|\[([^\]]+)\]\(([^)\s]+)\)/g;
  const parts = [];
  let last = 0, m, key = 0;
  while ((m = regex.exec(text))) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    if (m[1] !== undefined) {
      parts.push(<strong key={key++}>{m[1]}</strong>);
    } else {
      const href = m[3];
      parts.push(
        <a key={key++} href={href} className="underline text-[#D4AF37]" target={href.startsWith("http") ? "_blank" : undefined} rel="noreferrer">
          {m[2]}
        </a>
      );
    }
    last = regex.lastIndex;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

export default function AiChatWidget({ mode = "customer" }) {
  const storageKey = `ai_chat_session_${mode}`;
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(() => {
    let sid = localStorage.getItem(storageKey);
    if (!sid) { sid = newSessionId(); localStorage.setItem(storageKey, sid); }
    return sid;
  });
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const scrollRef = useRef(null);
  const copy = COPY[mode] || COPY.customer;

  useEffect(() => {
    if (!open || loaded) return;
    fetch(`${API}/ai-chat/history/${sessionId}`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : { messages: [] }))
      .then((d) => setMessages(d.messages || []))
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, [open, loaded, sessionId]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  const resetChat = () => {
    const sid = newSessionId();
    localStorage.setItem(storageKey, sid);
    setSessionId(sid);
    setMessages([]);
  };

  const send = async () => {
    const text = input.trim();
    if (!text || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setStreaming(true);
    try {
      const resp = await fetch(`${API}/ai-chat/message`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ session_id: sessionId, mode, message: text }),
      });
      if (!resp.ok || !resp.body) throw new Error("request failed");
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let acc = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data: ")) continue;
          try {
            const ev = JSON.parse(line.slice(6));
            if (ev.delta) {
              acc += ev.delta;
              setMessages((m) => {
                const copyM = [...m];
                copyM[copyM.length - 1] = { role: "assistant", content: acc };
                return copyM;
              });
            } else if (ev.error) {
              acc = acc || ev.error;
              setMessages((m) => {
                const copyM = [...m];
                copyM[copyM.length - 1] = { role: "assistant", content: acc };
                return copyM;
              });
            }
          } catch { /* partial json — ignore */ }
        }
      }
      if (!acc) {
        setMessages((m) => {
          const copyM = [...m];
          copyM[copyM.length - 1] = { role: "assistant", content: "Sorry, I couldn't respond just now. Please try again." };
          return copyM;
        });
      }
    } catch {
      setMessages((m) => {
        const copyM = [...m];
        copyM[copyM.length - 1] = { role: "assistant", content: "Connection issue — please try again." };
        return copyM;
      });
    } finally {
      setStreaming(false);
    }
  };

  return (
    <div className={mode === "customer" ? "public-light" : undefined}>
      {/* Floating toggle */}
      <button
        data-testid="ai-chat-toggle"
        onClick={() => setOpen((o) => !o)}
        aria-label={open ? "Close chat" : "Open chat"}
        className="fixed bottom-20 right-5 z-[70] w-14 h-14 rounded-full bg-[#D4AF37] text-black flex items-center justify-center shadow-[0_4px_24px_rgba(0,0,0,0.35)] hover:scale-105 transition-transform"
      >
        {open ? <X size={22}/> : <MessageCircle size={22}/>}
      </button>

      {open && (
        <div
          data-testid="ai-chat-panel"
          className="fixed bottom-[9.5rem] right-4 sm:right-5 z-[70] w-[calc(100vw-2rem)] sm:w-[380px] h-[520px] max-h-[calc(100vh-12rem)] bg-[#0A0A0A] border border-white/10 shadow-2xl flex flex-col"
        >
          {/* Header */}
          <div className="flex items-center gap-3 px-4 py-3 border-b border-white/10 bg-[#050505]">
            <div className="w-8 h-8 rounded-full bg-[#D4AF37]/15 flex items-center justify-center text-[#D4AF37]">
              <Sparkles size={16}/>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[#F5F2EB] text-sm font-serif">{copy.title}</div>
              <div className="text-[#A1A1AA] text-[10px] uppercase tracking-[0.18em]">{copy.sub}</div>
            </div>
            <button
              data-testid="ai-chat-reset"
              onClick={resetChat}
              title="New conversation"
              className="text-[#A1A1AA] hover:text-[#D4AF37] transition-colors p-1"
            >
              <RotateCcw size={15}/>
            </button>
          </div>

          {/* Messages */}
          <div ref={scrollRef} data-testid="ai-chat-messages" className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
            {messages.length === 0 && (
              <div className="text-[#A1A1AA] text-sm leading-relaxed bg-white/5 border border-white/5 p-3">
                {copy.greeting}
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[85%] px-3 py-2 text-sm leading-relaxed whitespace-pre-wrap break-words ${
                    m.role === "user"
                      ? "bg-[#D4AF37] text-black"
                      : "bg-white/5 border border-white/5 text-[#F5F2EB]"
                  }`}
                >
                  {m.content ? (m.role === "assistant" ? renderContent(m.content) : m.content) : (streaming && i === messages.length - 1 ? (
                    <span className="inline-flex gap-1 items-center py-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37] animate-bounce" style={{ animationDelay: "0ms" }}/>
                      <span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37] animate-bounce" style={{ animationDelay: "120ms" }}/>
                      <span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37] animate-bounce" style={{ animationDelay: "240ms" }}/>
                    </span>
                  ) : m.content)}
                </div>
              </div>
            ))}
          </div>

          {/* Input */}
          <div className="border-t border-white/10 p-3 flex gap-2 bg-[#050505]">
            <input
              data-testid="ai-chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder={copy.placeholder}
              disabled={streaming}
              className="flex-1 bg-[#0A0A0A] border border-white/10 px-3 py-2 text-sm text-[#F5F2EB] placeholder:text-[#52525B] focus:outline-none focus:border-[#D4AF37]/60"
            />
            <button
              data-testid="ai-chat-send"
              onClick={send}
              disabled={streaming || !input.trim()}
              className="w-10 h-10 flex items-center justify-center bg-[#D4AF37] text-black disabled:opacity-40 hover:brightness-110 transition"
            >
              <Send size={16}/>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
